// src/utils/systemColumns.js
// -----------------------------------------------------------------------------
// Centralized definitions & helpers for ColumnDraftManager "system" columns.
// Safe for enterprise use: pure utils, no Firestore writes, no metadata changes.
// -----------------------------------------------------------------------------

/**
 * Canonical system columns. IDs must match ColumnDraftManager.
 * Keep this list in sync with the ColumnDraftManager SYSTEM_DEFAULTS.
 */
export const SYSTEM_COLUMNS = [
  { id: 'sys:submissionId',       label: 'Submission ID' },
  { id: 'sys:submittedAt',        label: 'Submitted at'  },
  { id: 'sys:submitterId',        label: 'User ID'       },
  { id: 'sys:submitterSessionId', label: 'Session ID'    },
  { id: 'sys:organizationId',     label: 'Organization'  },
];

/** Quick predicate */
export function isSystemId(id) {
  return typeof id === 'string' && id.startsWith('sys:');
}

/** Normalize Firestore Timestamp | Date | ISO string → Date | null */
export function toDateSafe(tsLike) {
  if (!tsLike) return null;
  try {
    if (typeof tsLike?.toDate === 'function') return tsLike.toDate();
    if (tsLike instanceof Date) return isNaN(tsLike) ? null : tsLike;
    const d = new Date(tsLike);
    return isNaN(d) ? null : d;
  } catch {
    return null;
  }
}

/**
 * Resolve a system column value off a submission row.
 * - Pure read; never mutates input.
 * - Returns a string for easy cell rendering.
 */
export function resolveSystemValue(row, sysId) {
  switch (sysId) {
    case 'sys:submissionId': {
      const v = row?.id || row?.submissionId;
      return v ?? 'N/A';
    }

    case 'sys:submittedAt': {
      const d = toDateSafe(row?.submittedAt || row?.createdAt);
      return d ? d.toLocaleString() : 'N/A';
    }

    case 'sys:submitterId':
      return row?.submitterId ?? 'N/A';

    case 'sys:submitterSessionId':
      return row?.submitterSessionId ?? 'N/A';

    case 'sys:organizationId':
      return row?.organizationId ?? 'N/A';

    default:
      return 'N/A';
  }
}

/**
 * Comparator factory for system columns (used in table sorting).
 * Currently special-cases submittedAt; all else falls back to string compare.
 *
 * @param {'asc'|'desc'} order
 * @param {string} orderBy - column id
 * @returns {(a:any, b:any) => number}
 */
export function getSystemComparator(order = 'asc', orderBy) {
  const dir = order === 'asc' ? 1 : -1;

  if (orderBy === 'sys:submittedAt' || orderBy === 'submittedAt') {
    return (a, b) => {
      const da = toDateSafe(a?.submittedAt || a?.createdAt) || new Date(0);
      const db = toDateSafe(b?.submittedAt || b?.createdAt) || new Date(0);
      return (da - db) * dir;
    };
  }

  if (orderBy === 'sys:submissionId') {
    return (a, b) => String(a?.id || a?.submissionId || '')
      .localeCompare(String(b?.id || b?.submissionId || '')) * dir;
  }

  if (orderBy === 'sys:submitterId') {
    return (a, b) => String(a?.submitterId || '').localeCompare(String(b?.submitterId || '')) * dir;
  }

  if (orderBy === 'sys:submitterSessionId') {
    return (a, b) => String(a?.submitterSessionId || '').localeCompare(String(b?.submitterSessionId || '')) * dir;
  }

  if (orderBy === 'sys:organizationId') {
    return (a, b) => String(a?.organizationId || '').localeCompare(String(b?.organizationId || '')) * dir;
  }

  // default: stable string compare using resolved system values
  return (a, b) => {
    const va = String(resolveSystemValue(a, orderBy) ?? '');
    const vb = String(resolveSystemValue(b, orderBy) ?? '');
    return va.localeCompare(vb) * dir;
  };
}

